import React, { useState, useEffect } from "react";
import { navigate } from "vike/client/router";
import { useDispatch } from "react-redux";
import { timeAgo } from "#utils/utils";
import Votes from "#helpers/Votes";
import PostActions from "#components/forum/showPost/PostActions";
import { downvotePost, updatePost, upvotePost } from "#redux/slices/forumSlice";
import { SpoilerTag } from "#helpers/Tag";
import ChatBubble from "#assets/chat-bubble.svg";
import pin from "#assets/pin.svg";
import Button from "#helpers/Button";
import UserProvidedContent from "#helpers/richText/UserProvidedContent";
import { openModal, setModalDirty } from "#redux/slices/globalUISlice";
import BookCover from "#components/helpers/BookCover";
import Badge from "#components/helpers/Badge";
import Switch from "#components/helpers/Switch";
import { Menu, MenuItem } from "@mui/material";
import { getMaterialIcon } from "#utils/icons";
const ExpandMoreOutlinedIcon = getMaterialIcon("ExpandMoreOutlined");

const PostCard = ({ postData, variant, openInModal = true, commentId }) => {
  const dispatch = useDispatch();
  const [post, setPost] = useState(postData);
  const [isEditing, setIsEditing] = useState(false);
  const [newContent, setNewContent] = useState(post.content);
  const [newTitle, setNewTitle] = useState(post.title);
  const [isSpoiler, setIsSpoiler] = useState(post.is_spoiler);

  // Reading progress state variables
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [currentPage, setCurrentPage] = useState("");
  const [totalPages, setTotalPages] = useState("");
  const [currentPercent, setCurrentPercent] = useState("");
  const [readingUpdatePercent, setReadingUpdatePercent] = useState(post.reading_update_percent);
  const [progressType, setProgressType] = useState(post.reading_update_percent ? "percent" : "pages");

  if (!post) return;

  useEffect(() => {
    setPost(postData);
  }, [postData]);

  // Reading progress effects
  useEffect(() => {
    if (!isEditing || !post.reading_update_percent) return;

    if (progressType === "percent") {
      setCurrentPercent(post.reading_update_percent);
    }
  }, [isEditing, post.reading_update_percent, progressType]);

  useEffect(() => {
    if (!isEditing) return;

    const currentNum = Number(currentPage);
    const totalNum = Number(totalPages);
    const percentNum = Number(currentPercent);

    if (progressType === "pages") {
      if (currentNum > 0 && currentNum <= totalNum) {
        const percent = Math.round((currentNum / totalNum) * 100);
        setReadingUpdatePercent(percent);

        // Update title with percentage
        if (post.reading_update_percent !== null && post.reading_update_percent !== undefined) {
          setNewTitle(`Thoughts from ${percent}% (page ${currentNum})`);
        }
      } else {
        setReadingUpdatePercent(undefined);
      }
    }

    if (progressType === "percent") {
      if (percentNum > 0 && percentNum <= 100) {
        setReadingUpdatePercent(percentNum);

        // Update title with percentage
        if (post.reading_update_percent !== null && post.reading_update_percent !== undefined) {
          setNewTitle(`Thoughts from ${percentNum}%`);
        }
      } else {
        setReadingUpdatePercent(undefined);
      }
    }
  }, [currentPercent, currentPage, totalPages, progressType, isEditing]);

  const handleUpvote = (event) => {
    event.stopPropagation();
    dispatch(upvotePost(post.id)).then((data) => {
      setPost(data.payload);
    });
  };

  const handleDownvote = (event) => {
    event.stopPropagation();
    dispatch(downvotePost(post.id)).then((data) => {
      setPost(data.payload);
    });
  };

  const onChangeContent = (event) => {
    dispatch(setModalDirty(true));
    setNewContent(event.target.value);
  };

  const onChangeTitle = (event) => {
    dispatch(setModalDirty(true));
    setNewTitle(event.target.value);
  };

  const handleSubmitEdit = () => {
    const postData = {
      is_edited: true,
      content: newContent,
      title: newTitle,
      is_spoiler: isSpoiler,
      reading_update_percent: readingUpdatePercent,
    };
    dispatch(updatePost({ postId: post.id, postData }))
      .unwrap()
      .then((data) => {
        setPost(data);
        setIsEditing(false);
        dispatch(setModalDirty(false));
      });
  };

  const handleMenuClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = (event, type) => {
    event.preventDefault();
    event.stopPropagation();

    setMenuAnchor(null);
    if (type === "pages" || type === "percent") {
      setProgressType(type);
    }
  };

  const onClickPostCard = () => {
    if (isEditing) return;

    if (openInModal) {
      dispatch(
        openModal({ name: "showPost", data: { postId: post.id, postData: post, setCardPost: setPost, commentId } })
      );
    } else {
      navigate(`/posts/${post.id}${commentId ? `?commentId=${commentId}` : ""}`);
    }
  };

  return (
    <li
      key={post.uuid}
      onClick={onClickPostCard}
      className={`flex hover:cursor-pointer bg-white rounded-default p-4 md:p-5 border border-offBlack shadow-main gap-4 md:gap-6 ${isEditing ? "" : "max-h-[250px]"
        } text-left`}
    >
      {post.book && variant === "withImage" && !isEditing && (
        <div className="flex flex-none w-[54px] h-[80px] md:w-[60px] md:h-[99px]" onClick={(e) => e.stopPropagation()}>
          <BookCover book={post.book} size="w-[54px] h-[80px] md:w-[60px] md:h-[99px]" />
        </div>
      )}
      {post.badge && variant === "withImage" && (
        <>
          <div className="hidden md:block" onClick={(e) => e.stopPropagation()}>
            <Badge
              badge={{ image_url: post.badge.image_url, level: post.forum_type == "Side Quest" ? "champion" : "gold" }}
              size="large"
              onClick={() => {
                navigate(post.forum_link);
              }}
            />
          </div>
          <div className="block md:hidden" onClick={(e) => e.stopPropagation()}>
            <Badge
              badge={{ image_url: post.badge.image_url, level: post.forum_type == "Side Quest" ? "champion" : "gold" }}
              size="medium"
              onClick={() => {
                navigate(post.forum_link);
              }}
            />
          </div>
        </>
      )}
      <div className="flex flex-col gap-2 md:gap-3 flex-1">
        <div className="flex w-full text-xs text-gray justify-between items-center">
          <div className="flex gap-6 items-center">
            <a href={`/users/${post.username}`} onClick={(e) => e.stopPropagation()}>
              {post.username}
            </a>
            {/* <div>{timeAgo(post.created_at)}</div> */}
            {post.is_edited && <div>Edited</div>}
          </div>

          <div className="flex gap-3 items-center">
            {post.is_pinned && <img src={pin} alt="pin" className="w-4 h-4" />}
            {isEditing ? (
              <div className="hidden md:flex items-center gap-4">
                <div className="flex items-center">
                  <label className="mr-2 text-xs text-gray" htmlFor="hasSpoilers">
                    Spoilers?
                  </label>
                  <Switch id="hasSpoilers" isChecked={isSpoiler} onChange={() => setIsSpoiler(!isSpoiler)} />
                </div>
                <Button onClick={handleSubmitEdit} text="Update" color="bg-neonBlue text-offBlack" />
              </div>
            ) : (
              <PostActions post={post} setPost={setPost} onEdit={() => setIsEditing(true)} />
            )}
          </div>
        </div>
        <div className="flex gap-2.5 justify-between md:justify-normal">
          {isEditing ? (
            <input
              type="text"
              value={newTitle}
              onChange={onChangeTitle}
              className="flex-1 py-2 px-3 font-bold text-base"
              placeholder="Post Title"
            />
          ) : (
            <b>{post.is_flagged || post.deleted_at ? "[deleted]" : post.title}</b>
          )}
          {post.is_spoiler && !isEditing && <SpoilerTag />}
        </div>

        {isEditing && (
          <div className="md:hidden flex items-center gap-2 mb-2">
            <label className="text-xs text-gray" htmlFor="mobileHasSpoilers">
              Includes spoilers?
            </label>
            <Switch id="mobileHasSpoilers" isChecked={isSpoiler} onChange={() => setIsSpoiler(!isSpoiler)} />
          </div>
        )}

        {isEditing && post.reading_update_percent !== null && post.reading_update_percent !== undefined && (
          <div className="flex flex-col md:flex-row justify-between mb-1 gap-2">
            <div>
              <b>Progress:</b>
            </div>
            <div className="flex flex-row gap-3">
              {progressType === "pages" && (
                <>
                  <input
                    onChange={(e) => setCurrentPage(e.target.value)}
                    type="number"
                    value={currentPage}
                    placeholder="0"
                    id="current-page"
                    className="w-[50px] rounded-[15px] ml-0 p-2 my-auto [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <div className="self-center">of</div>
                  <input
                    onChange={(e) => setTotalPages(e.target.value)}
                    type="number"
                    value={totalPages}
                    placeholder="0"
                    id="total-pages"
                    className="w-[50px] rounded-[15px] ml-0 p-2 my-auto [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </>
              )}
              {progressType === "percent" && (
                <input
                  onChange={(e) => setCurrentPercent(e.target.value)}
                  type="number"
                  value={currentPercent}
                  placeholder="0"
                  id="current-percent"
                  className="w-[50px] rounded-[15px] ml-0 p-2 my-auto [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              )}
              <button onClick={handleMenuClick} id="progress-menu-button" className="flex flex-none items-center gap-1">
                {progressType === "pages" ? "pages" : "%"} <ExpandMoreOutlinedIcon fontSize="small" />
              </button>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                onClick={(e) => e.stopPropagation()}
                slotProps={{ paper: { className: `!rounded-default md:p-1` } }}
              >
                <MenuItem
                  classes={{ root: `!rounded-default hover:!bg-mainGreen` }}
                  onClick={(e) => handleMenuClose(e, "pages")}
                >
                  pages
                </MenuItem>
                <MenuItem
                  classes={{ root: `!rounded-default hover:!bg-mainGreen` }}
                  onClick={(e) => handleMenuClose(e, "percent")}
                >
                  %
                </MenuItem>
              </Menu>
            </div>
          </div>
        )}

        <div className={`text-[14px] md:text-[16px] ${isEditing ? "" : "text-ellipsis line-clamp-4"}`}>
          {isEditing ? (
            <textarea value={newContent} onChange={onChangeContent} rows={4} className="w-full py-3 px-4 my-auto" />
          ) : (
            <UserProvidedContent resource={post} content={post.content} type="post" />
          )}
        </div>

        {isEditing && (
          <div className="md:hidden flex justify-end mt-2">
            <Button onClick={handleSubmitEdit} text="Update" color="bg-neonBlue text-offBlack" />
          </div>
        )}

        {!isEditing && (
          <div className="flex gap-3 items-center font-bold">
            <Votes
              handleUpvote={handleUpvote}
              handleDownvote={handleDownvote}
              userVote={post.user_vote}
              upvotes={post.upvotes}
            />
            <div
              style={{
                boxShadow: `1.8px 1.8px 0 0 #9C9C9C`,
              }}
              className="items-center text-xs text-gray rounded-default border py-1 px-3 bg-white border-gray"
            >
              <img src={ChatBubble} alt="comments" className="inline" />
              {"  "}
              {post.comment_count}
            </div>
            <div className="text-gray text-xs">Reply</div>
          </div>
        )}
      </div>
    </li>
  );
};

export default PostCard;
